import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Icon, Modal } from "semantic-ui-react";
import i18nstring, { strings } from "../../i18n";
import { Button } from "../atoms/Button";
import ConfirmationPopup from "../atoms/ConfirmationPopup";
import { FormSectionLargeInput } from "../atoms/FormFields";
import { PickupTimeWindow } from "../molecules/TimeWindow";
import { EMPTY_DELIVERY_SCHEDULE } from "../../utils/PickupInstructions";

const SELECT_ALL_ZIPS_VALUE = "__SELECT_ALL_ZIPS__";

export default function PickupInstructionEditorModal({
  isOpen,
  draftInstruction,
  zipOptions,
  editorErrors = {},
  isSaving,
  timeWindowRef,
  onClose,
  onSave,
  onZipChange,
  onInstructionsChange,
  onScheduleChange
}) {
  const [zipDropdownOpen, setZipDropdownOpen] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setZipDropdownOpen(false);
      setShowConfirmCancel(false);
    }
  }, [isOpen]);

  const allZipValues = useMemo(() => {
    return Array.from(
      new Set(
        (zipOptions || [])
          .map(option => String(option?.value ?? option?.key ?? option?.text ?? ""))
          .filter(Boolean)
      )
    );
  }, [zipOptions]);

  const dropdownOptions = useMemo(() => {
    if (!allZipValues.length) {
      return zipOptions || [];
    }

    return [
      {
        key: SELECT_ALL_ZIPS_VALUE,
        value: SELECT_ALL_ZIPS_VALUE,
        text: i18nstring(strings.SELECT_ALL_ZIPS)
      },
      ...(zipOptions || [])
    ];
  }, [allZipValues, zipOptions]);

  const handleCancelAttempt = useCallback(() => {
    if (isSaving) {
      return;
    }

    setShowConfirmCancel(true);
  }, [isSaving]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmCancel(false);
    onClose?.();
  }, [onClose]);

  const handleZipSelectionChange = useCallback((value) => {
    const nextSelectedZips = Array.isArray(value) ? value : [];

    if (nextSelectedZips.includes(SELECT_ALL_ZIPS_VALUE)) {
      onZipChange?.(allZipValues);
      setZipDropdownOpen(false);
      return;
    }

    onZipChange?.(nextSelectedZips);
  }, [allZipValues, onZipChange]);

  const renderValidationMessage = (message = "") => (
    <div className="form_field_validation_message">
      {message || " "}
    </div>
  );

  return (
    <>
      <Modal
        className="pickup_instruction_modal"
        open={isOpen}
        size="small"
        onClose={handleCancelAttempt}
        closeOnDimmerClick={!isSaving}
        closeOnEscape={!isSaving}
      >
        <Modal.Header className="pickup_instruction_modal_header">
          {draftInstruction.id
            ? i18nstring(strings.EDIT_PICKUP_INSTRUCTION)
            : i18nstring(strings.ADD_PICKUP_INSTRUCTION)}
        </Modal.Header>
        <Modal.Content className="pickup_instruction_modal_body">
          <div className="pickup_instruction_modal_content">
            <div className="loc_form_field_group loc_form_instructions_group pickup_instruction_modal_field">
              <span className="loc_form_toggle_label">
                {i18nstring(strings.DELIVERY_INSTRUCTIONS)}
              </span>
              <span className="loc_form_toggle_label_desc">
                {i18nstring(strings.DELIVERY_INSTRUCTIONS_NOTE)}
              </span>
              <FormSectionLargeInput
                name="pickup_instructions_modal"
                value={draftInstruction.deliveryInstructions}
                placeholder={i18nstring(strings.DELIVERY_INSTRUCTIONS_GARDENER)}
                maxChar={600}
                onChange={event => onInstructionsChange?.(event.target.value)}
              />
              {renderValidationMessage(editorErrors.deliveryInstructions)}
            </div>

            <div className="loc_form_field_group pickup_instruction_modal_field pickup_instruction_modal_schedule_group">
              <label className="loc_form_field_label">
                {i18nstring(strings.MY_TIMES)}
              </label>
              <span className="loc_form_toggle_label_desc pickup_instruction_modal_schedule_note">
                {i18nstring(strings.TIME_SCHEDULES_PICKUP_NOTE)}
              </span>
              <PickupTimeWindow
                deliverySchedule={
                  draftInstruction.deliverySchedule || EMPTY_DELIVERY_SCHEDULE
                }
                updateZipSchedule={onScheduleChange}
                ref={timeWindowRef}
              />
              {renderValidationMessage(editorErrors.schedule)}
            </div>

            <div className="modal_form_field pickup_instruction_modal_field">
              <label className="modal_form_field_label">
                {i18nstring(strings.APPLY_TO_THESE_ZIPS)} ({i18nstring(strings.OPTIONAL)})
              </label>
              <Dropdown
                className="pickup_instructions_dropdown"
                fluid
                multiple
                selection
                search
                placeholder={i18nstring(strings.PICK_ZIP_CODE)}
                open={zipDropdownOpen}
                onOpen={() => setZipDropdownOpen(true)}
                onClose={() => setZipDropdownOpen(false)}
                options={dropdownOptions}
                value={draftInstruction.zips || []}
                onChange={(_, data) => handleZipSelectionChange(data.value)}
              />
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions className="pickup_instruction_modal_actions">
          <Button
            className="loc_form_save_button pickup_instructions_modal_button"
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving && <Icon loading name="spinner" />}
            {i18nstring(strings.SAVE_CHANGES)}
          </Button>
          <Button
            className="loc_form_cancel_button pickup_instructions_modal_button pickup_instructions_modal_button_secondary"
            onClick={handleCancelAttempt}
            disabled={isSaving}
          >
            {i18nstring(strings.CANCEL)}
          </Button>
        </Modal.Actions>
      </Modal>

      {showConfirmCancel && (
        <ConfirmationPopup
          title={i18nstring(strings.CONFIRMATION)}
          message={i18nstring(strings.CANCEL_LOCATION_CONFIRMATION_MESSAGE)}
          primaryButtonText={i18nstring(strings.CONFIRM)}
          onPrimaryButtonClicked={handleConfirmClose}
          open={showConfirmCancel}
          setOpen={setShowConfirmCancel}
        />
      )}
    </>
  );
}
